import { Download, X } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import useInstallPrompt from "@/hooks/useInstallPrompt";

/**
 * InstallPrompt: floating, dismissible "Install ConfirmPH" banner.
 *
 * Visible on supported browsers once the `beforeinstallprompt` event
 * has fired, and the user has not dismissed it in the last 14 days
 * (handled by the hook).  Hidden entirely once installed.
 */
export default function InstallPrompt() {
  const { canInstall, dismissed, promptInstall, dismiss } = useInstallPrompt();

  if (!canInstall || dismissed) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-3 z-[55] px-3 sm:left-auto sm:right-4 sm:bottom-4 sm:px-0 sm:w-[360px] pointer-events-none"
      data-testid="install-prompt"
    >
      <div className="pointer-events-auto bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 grid place-items-center flex-shrink-0 p-1">
          <BrandLogo variant="icon" className="w-full h-full" alt="ConfirmPH" testId="install-prompt-icon" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-slate-900 text-sm leading-tight">
            Install ConfirmPH
          </div>
          <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
            Add it to your home screen for one-tap access to bookings, rentals and notifications.
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => promptInstall()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-xs font-semibold hover:bg-[#143027]"
              data-testid="install-prompt-install-btn"
            >
              <Download className="w-3.5 h-3.5" /> Install
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="px-2.5 py-1.5 rounded-lg text-xs text-slate-600 hover:bg-slate-100"
              data-testid="install-prompt-dismiss-btn"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-slate-400 hover:text-slate-600 -mr-1 -mt-1"
          data-testid="install-prompt-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
